// Members Roster Types

export interface PositionOfTrust {
  id: string;
  name: string;
  label: string;
}

// Position of Trust options
export const POSITIONS_OF_TRUST: PositionOfTrust[] = [
  { id: "board_member", name: "board_member", label: "Board member" },
  { id: "junior_officer", name: "junior_officer", label: "Junior officer" },
  { id: "club_clerk", name: "club_clerk", label: "Club clerk" },
  { id: "chief_inspector", name: "chief_inspector", label: "Chief inspector" },
  { id: "race_officer", name: "race_officer", label: "Race officer" },
  { id: "accountant", name: "accountant", label: "Accountant" },
  { id: "commodore", name: "commodore", label: "Commodore" },
  {
    id: "training_officer",
    name: "training_officer",
    label: "Training officer",
  },
  {
    id: "cruising_officer",
    name: "cruising_officer",
    label: "Cruising officer",
  },
];

// Invoice destination options
export type InvoiceDestination =
  | "home_address"
  | "primary_member"
  | "billing_address"
  | "email";

export const INVOICE_DESTINATIONS: Array<{
  id: InvoiceDestination;
  label: string;
}> = [
  { id: "home_address", label: "Invoice to home address" },
  { id: "primary_member", label: "Invoice to primary member (home member)" },
  { id: "billing_address", label: "Invoice to billing address" },
  { id: "email", label: "Invoice by e-mail" },
];

// Member roster entry interface
export interface MemberRosterEntry {
  id: string;
  // Member details
  federation_membership_number: string;
  club_id: string;
  club_name?: string;
  membership_category: string;
  membership_start_date: string;
  membership_end_date: string;
  svp_membership_id: string; // Required
  position_of_trust?: string; // Position from POSITIONS_OF_TRUST

  // General billing details
  invoice_destination: InvoiceDestination; // Required
  street_address?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  billing_name?: string;
  billing_email?: string;
  billing_phone?: string;

  // Club-specific additional details
  honours_awards?: string; // Honours/awards (merit badges, decorations)
  storage_locker_key?: string;
  eye_color?: string;
  shoe_size?: string;

  // User/member reference
  user_id?: string;
  user?: {
    id: string | null;
    first_name?: string;
    last_name?: string;
    name?: string;
    email?: string;
    mobile?: string;
  } | null;
  // name and email usually derived from user object, but keeping optional just in case
  name?: string;
  email?: string;
  phone?: string;

  // Linked boats
  boats?: Array<{
    id: string;
    name: string;
    registration_number?: string;
  }>;

  // Status
  member_status: MemberStatus;

  // Timestamps
  created_at: string;
  updated_at: string;
}

// Member status enum
export enum MemberStatus {
  ACTIVE = 1,
  SUSPENDED = 2,
}

// Form state for add/edit
export interface MemberRosterFormState {
  // Member details
  federation_membership_number: string;
  club_id: string;
  membership_category: string;
  membership_start_date: string;
  membership_end_date: string;
  svp_membership_id: string;
  position_of_trust: string;
  name: string;
  email: string;
  phone: string;

  // General billing details
  invoice_destination: InvoiceDestination;
  street_address: string;
  postal_code: string;
  city: string;
  country: string;
  billing_name: string;
  billing_email: string;
  billing_phone: string;

  // Club-specific additional details
  honours_awards: string;
  storage_locker_key: string;
  eye_color: string;
  shoe_size: string;

  // User selection (for linking to existing user)
  user_id: string;

  // Status (only for edit mode)
  status?: string;

  [key: string]: string | InvoiceDestination | undefined;
}

// Query parameters for API
export interface MemberRosterQueryParams {
  club_id?: string;
  page?: number;
  limit?: number;
  search?: string; // Search through name, membership number, SVP ID
  position_of_trust?: string; // Filter by position
  created_at?: string; // Filter by created date
  status?: MemberStatus;
}

// Paginated response
export interface PaginatedMemberRosterResponse {
  data: MemberRosterEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Display interface for table
export interface MemberRosterTableData {
  id: string;
  member_name: string;
  member_email: string;
  membership_number: string;
  svp_id: string;
  position_of_trust: string;
  membership_category: string;
  status: "Pending" | "Active" | "Suspended";
  status_id: MemberStatus;
  anniversary_date: string;
  boats_count: number;
}
export interface MemberBoat {
  id: string;
  club_id: string;
  boater_id: string;
  name: string;
  type: string;
  registration_number: string;
  certificate_number: string;
  holder: string;
  unit: string;
  length: string;
  width: string;
  draft: string;
  weight: string;
  displacement: string;
  manufacturer: string;
  manufacturer_country: string;
  hull_number: string;
  hull_color: string;
  year_of_manufacture: string;
  engine: string;
  berths: string;
  insurance_company: string;
  sewage_system: string;
  sewage_tank_size: string;
  fuel_tank_capacity: string;
  fresh_water_tank_capacity: string;
  inspections: string[];
  status: number;
  created_at: string;
  updated_at: string;
  member: {
    id: string;
    name: string;
    email: string;
  };
}

export interface MemberBoatQueryParams {
  page?: number;
  limit?: number;
  search?: string;
}
